/**
 * lib/model-normalizer.ts — FRIDGE-110: THE model-number normalizer.
 *
 * This is the FRIDGE-17 rule, proven against real data in the 07/25/2026
 * cross-network match pass (88/88 applied pairs verified, one structural
 * mismatch caught and excluded BEFORE writing):
 *
 *   normalize: strip every non-alphanumeric, uppercase.
 *   reject (return null) when the result is:
 *     - ASIN-shaped (^B0[A-Z0-9]{8}$) — Amazon ids are not model numbers;
 *     - outside 5–20 characters — shorter is junk, longer is a title;
 *     - missing a letter or missing a digit — real appliance models have both.
 *
 * Rejection returns NULL, never a guess (Rule 3). A NULL normalized_model
 * states honestly "no reliable model key" — the join layers must skip it.
 *
 * Measured 07/27/2026 before the rebuild: of 3,433 stored values, 3,280
 * (95.5%) violated this rule — slugified titles (2,789 with the multi-hyphen
 * signature) and finish-suffix drift, written by amazon-paapi-v2 (2,111),
 * cj-product-feed (540), sftp-cj (267) and keepa-product-finder (240).
 * The write sites get fixed per-importer on FRIDGE-110; every importer must
 * route through THIS function rather than local variants.
 */

const ASIN_SHAPE = /^B0[A-Z0-9]{8}$/;

/** FRIDGE-924: exact feed-internal hex triplet, never a manufacturer model. */
export function isRetailerHexModel(model: string | null | undefined): boolean {
  return /^[A-F0-9]{2}-[A-F0-9]{4}-[A-F0-9]{12}$/i.test((model ?? '').trim());
}

/**
 * The CJ shape: an advertiser id, an underscore, and an Amazon ASIN
 * (`3883_B075QBS8DV`). Once non-alphanumerics are stripped it reads
 * `3883B075QBS8DV` — 14 characters with a letter and a digit, so it passed
 * every strict test and 461 rows (412 active) stored it as a MODEL JOIN KEY,
 * measured live 08/17/2026. Neither half is a model: the prefix is our own
 * plumbing and the suffix is an Amazon identifier the strict rule already
 * rejects on its own. `resolveDisplayModel` learned to see through the prefix
 * on 08/16 (FRIDGE-110, commit 70b8272f) and closed the four RENDER sites;
 * the join key was left carrying it, which is the more dangerous half —
 * a key that matches nothing real still matches ITSELF across feeds.
 */
const FEED_PREFIXED_ASIN_SHAPE = /^[0-9]+B0[A-Z0-9]{8}$/;

export function normalizeModelStrict(model: string | null | undefined): string | null {
  if (!model) return null;
  if (isRetailerHexModel(model)) return null;
  const norm = model.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (norm.length < 5 || norm.length > 20) return null;
  if (!/[A-Z]/.test(norm) || !/[0-9]/.test(norm)) return null;
  if (ASIN_SHAPE.test(norm)) return null;
  if (FEED_PREFIXED_ASIN_SHAPE.test(norm)) return null;
  return norm;
}

/**
 * The Amazon ASIN a product row's `model` column carries, or null when it
 * carries something else. Accepts the bare form and the feed-prefixed form,
 * because both name the SAME Amazon product — which is the whole point:
 * `3883_B075QBS8DV` and `B075QBS8DV` arrived through different feeds as two
 * active rows for one ASIN (FRIDGE-330), and no importer could see it because
 * each one only ever compared the string it held.
 *
 * The prefix is removed ONLY to identify the ASIN. It is never displayed and
 * never written back into a model column.
 */
export function asinFromModel(model: string | null | undefined): string | null {
  if (!model) return null;
  const bare = String(model).trim().toUpperCase().replace(/^[0-9]+_/, '');
  return ASIN_SHAPE.test(bare) ? bare : null;
}

/**
 * FRIDGE-110 rebuild, 08/16/2026 — recovering a model when `model` holds none.
 *
 * 2,353 rows store a bare ASIN in `model` and 461 store one behind a numeric
 * feed prefix, so normalizeModelStrict correctly returns NULL for them and the
 * join key stays empty even where the real model is sitting in the same row.
 *
 * THE RULE: TWO INDEPENDENT FIELDS MUST AGREE.
 * A candidate is a whitespace token of the row's own `title` that passes the
 * strict shape, AND that occupies the model slot of the row's own `seo_slug` —
 * the segment immediately before the `-fin<7 digits>` suffix. Both must hold.
 * If more than one distinct token qualifies the row is skipped as ambiguous.
 *
 * WHY BOTH, AND WHY THE FIN SLOT SPECIFICALLY
 * Neither field is trustworthy alone.
 *  - The slug's model slot can be LESS specific than the real model: the row
 *    slugged `midea-compact-refrigerator-mrb19b7-fin0000958` is titled
 *    "Midea MRB19B7AWW …". Taking the slug would write MRB19B7 as the join key
 *    and merge finishes that are different products — the exact trust-destroying
 *    outcome this column exists to prevent. Requiring the token to come from the
 *    title keeps full precision; requiring an exact token match (not a substring)
 *    is what rejects MRB19B7 against a title that says MRB19B7AWW.
 *  - The title alone would accept measurement words: "36-inch" passes the shape
 *    test. Anchoring to the slug's model slot rejects those, because the mint
 *    puts the model there and nothing else.
 *
 * Checked against live data 08/16/2026: 284 rows qualified, 0 ambiguous, and on
 * 107 of them the recovered value MATCHED the value already stored — the rule
 * reproducing known-good values it was not shown. A hand read of 14 at random
 * returned 14 real OEM models. The remaining rows are left NULL: where the two
 * fields do not agree there is no evidence, and a guess is worse than a gap.
 */
export function recoverModelFromTitleAndSlug(
  title: string | null | undefined,
  seoSlug: string | null | undefined,
): string | null {
  const slug = String(seoSlug ?? '');
  if (!/-fin\d{7}$/.test(slug)) return null;
  const slugKey = slug.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const hits = new Set<string>();
  for (const word of String(title ?? '').split(/\s+/)) {
    const tok = word.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (tok.length < 5 || tok.length > 20) continue;
    if (!/[A-Z]/.test(tok) || !/[0-9]/.test(tok)) continue;
    if (ASIN_SHAPE.test(tok)) continue;
    // tok is alphanumeric only, so it cannot carry regex metacharacters.
    if (new RegExp(`${tok}FIN\\d{7}$`).test(slugKey)) hits.add(tok);
  }
  return hits.size === 1 ? [...hits][0] : null;
}

/**
 * The value `products.normalized_model` must hold for a row: the strict
 * normalization of `model`, or — only when that yields nothing — a model the
 * row's own title and slug agree on. NULL when neither produces evidence.
 *
 * This is the single rule the rebuild applies. Anything that rewrites the
 * column must call THIS, not normalizeModelStrict alone: a strict-only pass
 * would clear the 284 rows recovered on 08/16/2026 and put the join key back
 * to empty on every one of them.
 */
export function normalizedModelForRow(
  model: string | null | undefined,
  title: string | null | undefined,
  seoSlug: string | null | undefined,
): string | null {
  return normalizeModelStrict(model) ?? recoverModelFromTitleAndSlug(title, seoSlug);
}

/**
 * FRIDGE-167 slice 2 — the LOOSE normalizer: strip non-alphanumerics,
 * lowercase, no validity rejection. This exact function existed three times
 * (keepa-link-asins, keepa-csv-mint-canonicals, lib/pip/normalize); they now
 * share this one. It is for FUZZY comparison of two strings both believed to
 * be models — it deliberately does NOT reject junk, because its callers
 * compare against known-good references. Never use it to WRITE a model key;
 * that is normalizeModelStrict's job.
 */
export function normalizeModelLoose(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/[^A-Za-z0-9]/g, '').toLowerCase();
}
